import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../utils/api';
import buildImageUrl from '../utils/imageUrl';
import './ItemDetail.css';

const ItemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, getItemQuantity } = useCart();
  const [item, setItem] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [hasMainImageError, setHasMainImageError] = useState(false);

  useEffect(() => {
    fetchItem();
  }, [id]);

  useEffect(() => {
    setHasMainImageError(false);
  }, [selectedImage?.image_url]);

  const fetchItem = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/items/${id}`);
      setItem(response.data);
      const primaryImage = response.data.images?.find((img) => img.is_primary) || response.data.images?.[0];
      setSelectedImage(primaryImage);
    } catch (err) {
      setError('Failed to load item');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(item, quantity);
    setQuantity(1);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error || !item) {
    return <div className="error-message">{error || 'Item not found'}</div>;
  }

  const inCartQty = getItemQuantity(item.id);
  const remainingStock = item.stock_quantity - inCartQty;
  const canAddToCart = item.is_available && remainingStock > 0;
  const selectedImageUrl = buildImageUrl(selectedImage?.image_url);

  return (
    <div className="item-detail">
      <button onClick={() => navigate(-1)} className="back-btn">
        ← Back
      </button>

      <div className="item-content">
        <div className="item-images">
          <div className="main-image">
            {selectedImageUrl && !hasMainImageError ? (
              <img
                src={selectedImageUrl}
                alt={item.name}
                onError={() => setHasMainImageError(true)}
              />
            ) : (
              <div className="image-placeholder">No Image</div>
            )}
          </div>
          {item.images && item.images.length > 1 && (
            <div className="image-thumbnails">
              {item.images.map((img) => (
                buildImageUrl(img.image_url) ? (
                  <div
                    key={img.id}
                    className={`thumbnail ${selectedImage?.id === img.id ? 'active' : ''}`}
                    onClick={() => setSelectedImage(img)}
                  >
                    <img
                      src={buildImageUrl(img.image_url)}
                      alt={item.name}
                    />
                  </div>
                ) : null
              ))}
            </div>
          )}
        </div>

        <div className="item-details">
          <h1>{item.name}</h1>
          {item.category && <p className="category">{item.category}</p>}
          <p className="price">${item.price.toFixed(2)}</p>

          {item.description && (
            <div className="description">
              <h3>Description</h3>
              <p>{item.description}</p>
            </div>
          )}

          <div className="stock-info">
            {canAddToCart ? (
              <p className="in-stock">
                In Stock ({item.stock_quantity} available{inCartQty > 0 ? `, ${inCartQty} in cart` : ''})
              </p>
            ) : item.stock_quantity > 0 && inCartQty >= item.stock_quantity ? (
              <p className="out-of-stock">All stock already in cart</p>
            ) : (
              <p className="out-of-stock">Out of Stock</p>
            )}
          </div>

          {canAddToCart && (
            <div className="purchase-section">
              <div className="quantity-selector">
                <label>Quantity:</label>
                <div className="quantity-controls">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span>{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(remainingStock, quantity + 1))}
                    disabled={quantity >= remainingStock}
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                className="btn btn-primary add-to-cart-btn"
                onClick={handleAddToCart}
              >
                {addedToCart ? '✓ Added to Cart!' : 'Add to Cart'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemDetail;
